/**
 * Structural passes over already-sanitised document HTML (see markdown.ts —
 * DOMPurify has run before anything here touches the string).
 *
 * These read and rewrite the document as a tree rather than by pattern-matching
 * its text, so neither one depends on the wording of the content it is editing.
 */

export type OutlineItem = { id: string; text: string; level: 2 | 3 }

const parse = (html: string) =>
  new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html')

const serialise = (doc: Document) => doc.body.innerHTML

/** "Screen Time and Our Program" -> "screen-time-and-our-program" */
function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
  // A heading of only punctuation would slug to '' and collide with the next
  // such heading; "section" plus the de-duplicating suffix keeps ids unique.
  return slug || 'section'
}

/**
 * Gives every heading a stable id and reports the document's outline, so the
 * contents rail can link into a document whose headings are authored content
 * and carry no ids of their own.
 *
 * Returns the rewritten HTML — the ids have to exist in the DOM for the links
 * to land, so callers must render this and not the string they passed in.
 */
export function extractOutline(html: string): { html: string; outline: OutlineItem[] } {
  const doc = parse(html)
  const outline: OutlineItem[] = []
  const used = new Map<string, number>()

  for (const el of doc.body.querySelectorAll('h2, h3')) {
    const text = (el.textContent ?? '').trim()
    if (!text) continue

    const base = slugify(text)
    // Two sections can legitimately share a title (several forms repeat
    // "Declaration"); an id collision would send every link to the first one.
    const seen = used.get(base) ?? 0
    used.set(base, seen + 1)
    const id = seen === 0 ? base : `${base}-${seen + 1}`

    el.setAttribute('id', id)
    outline.push({ id, text, level: el.tagName === 'H2' ? 2 : 3 })
  }

  return { html: serialise(doc), outline }
}

/**
 * Drops a leading blockquote.
 *
 * The server prepends the draft notice to the markdown as a blockquote, before
 * any authored content, so that the notice survives into the downloaded .doc.
 * On screen the same sentence is already carried by DraftBanner, and the page
 * would otherwise state it twice.
 *
 * Deliberately structural, not textual: it removes "the blockquote the server
 * put first", never "the paragraph matching this sentence". Matching on the
 * wording would add a fourth hardcoded copy of the notice — the exact drift the
 * ledger's F7 finding is about — and would silently stop working the day the
 * wording changes.
 */
/**
 * Puts each table in its own horizontally scrollable box.
 *
 * A markdown table has no width it can shrink to — a checklist with six columns
 * is as wide as its content demands, and without this it pushes the whole page
 * sideways rather than scrolling within its own bounds.
 */
export function wrapTables(html: string): string {
  const doc = parse(html)
  for (const table of doc.body.querySelectorAll('table')) {
    // Idempotent: re-running must not nest a second box around the first.
    if (table.parentElement?.classList.contains('table-scroll')) continue
    const box = doc.createElement('div')
    box.className = 'table-scroll'
    table.replaceWith(box)
    box.append(table)
  }
  return serialise(doc)
}

export function dropLeadingBlockquote(html: string): string {
  const doc = parse(html)
  const first = doc.body.firstElementChild
  if (first?.tagName === 'BLOCKQUOTE') first.remove()
  return serialise(doc)
}
